import { useCallback, useState } from "react";
import { RegisteredVehicle } from "common/graphql";
import { VehicleTypeValue } from "@app.automotus.io/components/common";
import { EditBuffer, NewVehicleRow, VehicleRowErrors, VehicleTableRow } from "../vehicles.types";
import { validateVehicleRow } from "../validateVehicleRow";

interface UseVehicleEditModeOptions {
  existingVehicles: RegisteredVehicle[];
  newRows: NewVehicleRow[];
  onSaveEdit: (id: string, data: EditBuffer) => Promise<void>;
}

export interface UseVehicleEditModeResult {
  editingRowId: string | null;
  editBuffer: EditBuffer | null;
  editErrors: VehicleRowErrors;
  isSaving: boolean;
  saveError: string | null;
  handleStartEdit: (row: VehicleTableRow) => void;
  handleEditBufferChange: (patch: Partial<EditBuffer>) => void;
  handleSave: () => Promise<void>;
  handleCancelEdit: () => void;
}

export function useVehicleEditMode({
  existingVehicles,
  newRows,
  onSaveEdit,
}: UseVehicleEditModeOptions): UseVehicleEditModeResult {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editBuffer, setEditBuffer] = useState<EditBuffer | null>(null);
  const [editErrors, setEditErrors] = useState<VehicleRowErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const runValidation = useCallback(
    (id: string, buffer: EditBuffer): VehicleRowErrors => {
      const otherExisting = existingVehicles.filter((v) => v.id !== id);
      const fakeRow: NewVehicleRow = {
        id,
        ...buffer,
        category: "commercial",
        errors: {},
      };
      return validateVehicleRow(fakeRow, newRows, otherExisting).errors;
    },
    [existingVehicles, newRows],
  );

  const handleStartEdit = useCallback(
    (row: VehicleTableRow) => {
      const buffer: EditBuffer = {
        stateCode: row.stateCode,
        licensePlate: row.licensePlate,
        make: row.make ?? "",
        model: row.model ?? "",
        type: (row.type as VehicleTypeValue | "") ?? "",
      };
      setEditingRowId(row.id);
      setEditBuffer(buffer);
      setSaveError(null);
      setEditErrors(runValidation(row.id, buffer));
    },
    [runValidation],
  );

  const handleEditBufferChange = useCallback(
    (patch: Partial<EditBuffer>) => {
      if (!editBuffer || !editingRowId) {
        return;
      }
      const updated = { ...editBuffer, ...patch };
      setEditBuffer(updated);
      setEditErrors(runValidation(editingRowId, updated));
    },
    [editBuffer, editingRowId, runValidation],
  );

  const handleSave = useCallback(async () => {
    if (!editBuffer || !editingRowId) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveEdit(editingRowId, editBuffer);
      setEditingRowId(null);
      setEditBuffer(null);
      setEditErrors({});
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [editBuffer, editingRowId, onSaveEdit]);

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    setEditBuffer(null);
    setEditErrors({});
    setSaveError(null);
  }, []);

  return {
    editingRowId,
    editBuffer,
    editErrors,
    isSaving,
    saveError,
    handleStartEdit,
    handleEditBufferChange,
    handleSave,
    handleCancelEdit,
  };
}
